// Helpers for the simple weekly cron pattern used by predefined reports.
// Cron field order: minute hour day-of-month month day-of-week
// Example: "0 0 * * 0" => every Sunday at midnight.

/*
	*   *   *   *   *
	-   -   -   -   -
	|   |   |   |   |
	|   |   |   |   |
	|   |   |   |   +----- day of week (0-7) (Sunday = 0 or 7) (or SUN-SAT)
	|   |   |   +--------- month (1-12) (or JAN-DEC)
	|   |   +------------- day of month (1-31)
	|   +----------------- hour (0-23)
	+--------------------- minute (0-59)
 */

/**
 * Array of Day names for a list box. Use ID for cron number.
 * @type {Array<{id: number, text: string}>}
 */
export const DAY_NAMES = [
	{ id: 0, text: 'Sunday' },
	{ id: 1, text: 'Monday' },
	{ id: 2, text: 'Tuesday' },
	{ id: 3, text: 'Wednesday' },
	{ id: 4, text: 'Thursday' },
	{ id: 5, text: 'Friday' },
	{ id: 6, text: 'Saturday' },
];

/**
 * Array of Month names for a ListBox. Use ID for cron number.
 * @type {[{id: number, text: string}]}
 */
export const MONTH_NAMES = [
	{ id: 0, text: "January" },
	{ id: 1, text: "February" },
	{ id: 2, text: "March" },
	{ id: 3, text: "April" },
	{ id: 4, text: "May" },
	{ id: 5, text: "June" },
	{ id: 6, text: "July" },
	{ id: 7, text: "August" },
	{ id: 8, text: "September" },
	{ id: 9, text: "October" },
	{ id: 10, text: "November" },
	{ id: 11, text: "December" }
];

/**
 * Array of Frequencies for a ListBox. Use ID for cron number.
 * @type {[{id: number, text: string}]}
 */
export const FREQUENCY_NAMES = [
	{ id: 0, text: "On-Demand" },
	{ id: 1, text: "Daily" },
	{ id: 2, text: "Weekly" },
	{ id: 3, text: "Monthly" },
	{ id: 4, text: "Quarterly" },
	{ id: 5, text: "Yearly" },
];

/**
 * Array of options for when a report can be run when monthly
 * @type {Array<{id: number, text: string, minute: number|string, hour: number|string, dom: number|string, month: number|string, dow: number|string}>}
 */
export const MONTHLY = [
	//{ id: 0, text: 'First of the month', 'minute': 0, 'hour': 0, 'dom': '1', 'month': '*', 'dow': '*' },//'0 0 1 * *' - At 12:00 AM, on day 1 of the month (this item made no sense)
	{ id: 1, text: 'End of the month', 'minute': 59, 'hour': 23, 'dom': 'L', 'month': '*', 'dow': '*' },//'59 23 L * *' - At 11:59 PM, on the last day of the month
];

/**
 * Creates an array of days for a list box
 * @returns {Array<{id: number, text: string}>}
 */
export function getDays()
{
	const days = [];
	for (let i = 1; i < 32; i++)
	{
		days.push({ id: i, text: i });
	}
	return days;
}

/**
 * Parse a weekly cron expression into its parts.
 * Returns null for anything that isn't the "specific minute/hour, weekly" shape,
 * so callers can fall back to showing the raw expression.
 *
 * @param {string} expression
 * @returns {{ minute: number, hour: number, dow: number|null }|null}
 */
export function parseWeeklyCron(expression)
{
	if (!expression) return null;

	const parts = expression.trim().split(/\s+/);
	if (parts.length !== 5) return null;

	const [minute, hour, dom, month, dow] = parts;

	// Only the weekly pattern is editable through the UI.
	if (dom !== '*' || month !== '*') return null;

	const min = Number(minute);
	const hr = Number(hour);
	if (Number.isNaN(min) || Number.isNaN(hr)) return null;

	let day = dow === '*' ? null : Number(dow);
	if (day === 7) day = 0; // cron treats 0 and 7 as Sunday
	if (day !== null && (Number.isNaN(day) || day < 0 || day > 6)) return null;

	return { minute: min, hour: hr, dow: day };
}

/**
 * Parse a weekly cron expression into its parts.
 * Returns null for anything that isn't the "specific minute/hour, weekly" shape,
 * so callers can fall back to showing the raw expression.
 *
 * @param {string} expression
 * @returns {{ minute: number|string|null, hour: number|string|null, dow: number|string|null, month: number|string|null, dom: number|string|null }|null}
 */
export function parseCron(expression)
{
	if (!expression) return null;

	const parts = expression.trim().split(/\s+/);
	if (parts.length !== 5) return null;

	const [minute, hour, dom, month, dow] = parts;

	return {
		"minute": isNaN(minute) ? minute : Number(minute),
		"hour": isNaN(hour) ? hour : Number(hour),
		"dow": isNaN(dow) ? dow : Number(dow),
		"dom": isNaN(dom) ? dom : Number(dom),
		"month": isNaN(month) ? month : Number(month) };
}

/**
 * Build a weekly cron expression from form values.
 *
 * @param {{ minute: number, hour: number, dow: number|null }} param0
 * @returns {string}
 */
export function buildWeeklyCron({ minute, hour, dow })
{
	const dayOfWeek = dow === null || dow === undefined ? '*' : dow;
	return `${minute} ${hour} * * ${dayOfWeek}`;
}

/**
 * Build a monthly cron expression from form values.
 *
 * @param {{ minute: number, hour: number, dom: number|string|null }} param0
 * @returns {string}
 */
export function buildMonthlyCron({ minute, hour, dom })
{
	const dayOfMonth = !dom ? '*' : dom;
	return `${minute} ${hour} ${dayOfMonth} * *`;
}

/**
 * Build a daily cron expression from form values.
 *
 * @param {{ minute: number, hour: number }} param0
 * @returns {string}
 */
export function buildDailyCron({ minute, hour})
{
	return `${minute} ${hour} * * *`;
}

/**
 * Render a cron expression as readable text, falling back to the raw
 * expression when it doesn't match the weekly pattern.
 *
 * @param {string} expression
 * @returns {string}
 */
export function cronToHuman(expression)
{
	const parsed = parseWeeklyCron(expression);
	if (!parsed) return expression || '—';

	const time = formatTime(parsed.hour, parsed.minute);

	if (parsed.dow === null) return `daily at ${time}`;

	const day = DAY_NAMES.find(d => d.id === parsed.dow)?.text ?? '';
	return `weekly on ${day} at ${time}`;
}

export function getCronFrequencyText(cronExpression)
{
	const daily = '59 23 * * *'; // At 11:59 PM, every day
	const weekly = '59 23 * * 6'; // At 11:59 PM, only on Saturday
	const monthly = '59 23 L * *'; // At 11:59 PM, on the last day of the month
	const quarterly = '59 23 L 3,6,9,12 *'; // At 11:59 PM on the last day of March, June, September, and December
	const yearly    = '59 23 L 12 *'; // At 11:59 PM, on the last day of December

	switch (cronExpression) {
		case daily:
			return 'Daily'
		case weekly:
			return 'Weekly'
		case monthly:
			return 'Monthly'
		case quarterly:
			return 'Quarterly'
		case yearly:
			return 'Yearly'
	}

	return 'Unknown'
}

function formatTime(hour, minute)
{
	const period = hour < 12 ? 'AM' : 'PM';
	const h = hour % 12 === 0 ? 12 : hour % 12;
	const m = String(minute).padStart(2, '0');
	return `${h}:${m} ${period}`;
}
